import { useState } from "react"

import { Bee } from "./BeeKit"
import { InputWithCannedResponses } from "./InputWithCannedResponses"
import { Reaction, ReactionType } from "./Reaction"
import { SelectableIconBox } from "./SelectableIconBox"
import { ContactUs, Modal } from "~/src/components"
import { RailsForm } from "~/src/components/RailsForm"
import { appClient } from "~/src/lib/appClients"

const CANNED_RESPONSES = ["Thank you for the gift!", "I love it!", "This made my day."]

export type ThankYouNoteFormProps = {
  submitPath: string
  formSubmitted: boolean
  onReactionChanged: (reaction: string) => void
  defaultReaction?: string
  onFormSubmit?: (e) => void
  senderName: string
  senderEmail: string
  contactUsUrl: string
  brilliantUrl: string
  viaRecipient: boolean
}

export function ThankYouNoteForm(props: ThankYouNoteFormProps) {
  const { viaRecipient, submitPath, formSubmitted, onReactionChanged, onFormSubmit, defaultReaction } = props

  const [message, setMessage] = useState<string>("")
  const [reaction, setReaction] = useState<string>(defaultReaction || "")
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)

  const setReactionState = (reaction: string) => {
    setReaction(reaction)
    onReactionChanged(reaction)
  }

  return (
    <div>
      <RailsForm method="post" action={submitPath} onSubmit={onFormSubmit}>
        <div className="mt-4 flex h-fit w-fit flex-col gap-6 rounded-md bg-white p-4 md:m-4 md:p-16">
          <div className="flex max-w-full flex-col">
            <h2 className="text-base font-medium">
              {viaRecipient
                ? "Want to send a personalized message to your sender?"
                : "Want to send a personalized message to your company?"}
            </h2>
            <InputWithCannedResponses
              name="message"
              containerClassName="mt-3 md:min-h-[138px]"
              onResponseChange={setMessage}
              placeholder="Add your message or get started with one of the responses below"
              cannedResponses={CANNED_RESPONSES}
            />
          </div>

          <div className="flex flex-col gap-4">
            <h2 className="text-base font-medium">
              {viaRecipient ? "What did you think of your gift?" : "What did you think of your order?"}
            </h2>
            <div className="grid grid-cols-2 place-items-stretch gap-4 md:grid-cols-4">
              <ReactionSelector
                reaction={ReactionType.HEART_EYES}
                text="Loved it"
                defaultChecked={ReactionType.HEART_EYES == reaction}
                setReaction={setReactionState}
              />
              <ReactionSelector
                reaction={ReactionType.PARTY_POPPER}
                text="Great surprise"
                defaultChecked={ReactionType.PARTY_POPPER == reaction}
                setReaction={setReactionState}
              />
              <ReactionSelector
                reaction={ReactionType.STAR}
                text="Super cool"
                defaultChecked={ReactionType.STAR == reaction}
                setReaction={setReactionState}
              />
              <ReactionSelector
                reaction={ReactionType.SMILING_FACE}
                text="Totally delightful"
                defaultChecked={ReactionType.SMILING_FACE == reaction}
                setReaction={setReactionState}
              />
            </div>
            <Bee.Button
              type="submit"
              className="text-sm text-white disabled:bg-gray-300"
              disabled={(!reaction.length && !message) || formSubmitted}
            >
              <span>Send note</span>
            </Bee.Button>

            <span className="text-sm text-gray-500">
              Need help with something? Want to get in touch?
              <button type="button" onClick={() => setModalIsOpen(true)} className="text-blue-600">
                &nbsp;Contact Brilliant
              </button>
            </span>
          </div>
        </div>
      </RailsForm>

      <Modal open={modalIsOpen} onClose={() => setModalIsOpen(false)} className="w-144 bg-white">
        <FeedbackModalContent {...props} />
      </Modal>
    </div>
  )
}

type ReactionSelectorProps = {
  reaction: ReactionType
  text: string
  defaultChecked: boolean
  setReaction: (reaction: ReactionType) => void
}

function ReactionSelector({ reaction, text, defaultChecked, setReaction }: ReactionSelectorProps) {
  return (
    <SelectableIconBox
      name="reaction"
      value={reaction}
      icon={<Reaction reaction={reaction} className="h-8 w-8" />}
      text={text}
      defaultChecked={defaultChecked}
      onSelect={() => setReaction(reaction)}
    />
  )
}

type FeedbackModalContentProps = {
  senderName: string
  senderEmail: string
  contactUsUrl: string
  brilliantUrl: string
}

function FeedbackModalContent({ contactUsUrl, brilliantUrl, senderName, senderEmail }: FeedbackModalContentProps) {
  const [feedbackSent, setFeedbackSent] = useState<boolean>(false)

  if (feedbackSent) {
    return (
      <div className="flex flex-col items-center gap-2">
        <h1 className="text-2xl text-gray-900">Thanks!</h1>
        <p className="text-center text-gray-700">
          We&apos;ve received your message and a member of our team will reach out shortly.
        </p>
      </div>
    )
  } else {
    return (
      <ContactUs
        url={contactUsUrl}
        brilliantUrl={brilliantUrl}
        name={senderName}
        email={senderEmail}
        formClassName="md:p-0 p-0 md:grid-cols-1"
        onSubmit={async (data) => {
          await appClient.post(contactUsUrl, data)
          setFeedbackSent(true)
        }}
      />
    )
  }
}
